import { useState, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import toast from "react-hot-toast";
import getTime from "../../../helpers/getTime.js";
import { addComment, updateComment, deleteComment, downloadAttachment, getActivityLogs } from "../../../api/ticketApi.js";
import ConfirmDialog from "./ConfirmDialog";
import "./CommentBox.css";
import "./ActivityLogs.css";

/**
 * Enhanced Comment Box with Nested Replies Support
 * 100% API Driven - NO DUMMY DATA
 */
const CommentBox = ({ ticket, ticketId, onCommentAdded }) => {
    const { user, isSuperAdmin } = useAuth();
    const [activeTab, setActiveTab] = useState("comments"); // "comments" or "activity"
    const [commentText, setCommentText] = useState("");
    const [isInternal, setIsInternal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState("");
    const [replyingTo, setReplyingTo] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        commentId: null,
    });
    const [isDeletingComment, setIsDeletingComment] = useState(false);
    const [files, setFiles] = useState([]);
    const [replyFiles, setReplyFiles] = useState({});
    // Activity logs state
    const [activityLogs, setActivityLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);

    /**
     * Fetch activity logs
     */
    useEffect(() => {
        if (activeTab === "activity" && ticketId) {
            fetchActivityLogs();
        }
    }, [activeTab, ticketId]);

    const fetchActivityLogs = async () => {
        try {
            setLoadingLogs(true);
            const response = await getActivityLogs(ticketId);
            if (response.success) {
                setActivityLogs(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching activity logs:", error);
        } finally {
            setLoadingLogs(false);
        }
    };

    /**
     * Get action icon for activity log
     */
    const getActionIcon = (action) => {
        switch (action) {
            case 'status':
                return 'las la-exchange-alt';
            case 'reopened':
                return 'las la-redo';
            case 'assigned':
                return 'las la-user-plus';
            case 'attachment':
                return 'las la-paperclip';
            case 'comment':
                return 'las la-comment';
            case 'created':
                return 'las la-plus-circle';
            case 'updated':
                return 'las la-edit';
            case 'deleted':
                return 'las la-trash';
            default:
                return 'las la-circle';
        }
    };

    /**
     * Get action color for activity log
     */
    const getActionColor = (action) => {
        switch (action) {
            case 'status':
                return '#3bac75';
            case 'reopened':
                return '#17a2b8';
            case 'assigned':
                return '#007bff';
            case 'attachment':
                return '#ffc107';
            case 'comment':
                return '#17a2b8';
            case 'created':
                return '#28a745';
            case 'updated':
                return '#6c757d';
            case 'deleted':
                return '#dc3545';
            default:
                return '#6c757d';
        }
    };

    /**
     * Handle file selection
     */
    const handleFileChange = (e, isReply = false) => {
        const selectedFiles = Array.from(e.target.files);
        
        // Validate file count (max 5 files)
        if (selectedFiles.length > 5) {
            toast.error("You can upload maximum 5 files.");
            return;
        }

        // Validate file sizes (max 50MB each)
        const maxSize = 50 * 1024 * 1024; // 50MB
        for (let file of selectedFiles) {
            if (file.size > maxSize) {
                toast.error(`File ${file.name} is too large. Maximum file size is 50MB.`);
                return;
            }
        }

        if (isReply) {
            const replyId = replyingTo;
            setReplyFiles(prev => ({
                ...prev,
                [replyId]: selectedFiles
            }));
        } else {
            setFiles(selectedFiles);
        }
        toast.success(`${selectedFiles.length} file(s) selected`);
    };

    /**
     * Remove a selected file
     */
    const removeFile = (index, isReply = false, replyId = null) => {
        if (isReply && replyId) {
            setReplyFiles(prev => {
                const newFiles = { ...prev };
                if (newFiles[replyId]) {
                    newFiles[replyId] = newFiles[replyId].filter((_, i) => i !== index);
                }
                return newFiles;
            });
        } else {
            setFiles(prev => prev.filter((_, i) => i !== index));
        }
        toast.success("File removed");
    };

    /**
     * Get file icon based on extension
     */
    const getFileIcon = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'las la-image';
        if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'].includes(ext)) return 'las la-video';
        if (ext === 'pdf') return 'las la-file-pdf';
        return 'las la-file';
    };

    /**
     * Post a new comment or reply - ALL DATA FROM API
     */
    const postComment = async (parentId = null) => {
        const text = parentId ? replyText : commentText;
        const commentFiles = parentId ? (replyFiles[parentId] || []) : files;
        
        if (!text.trim() && commentFiles.length === 0) {
            toast.error("Please enter a comment or attach a file");
            return;
        }

        try {
            setSubmitting(true);

            // Create FormData for file upload
            const formData = new FormData();
            formData.append('ticketId', ticketId);
            formData.append('comment', text || '');
            formData.append('isInternal', isSuperAdmin && isInternal ? 'true' : 'false');
            if (parentId) {
                formData.append('parentCommentId', parentId);
            }

            // Append files
            commentFiles.forEach((file) => {
                formData.append('attachments', file);
            });

            const response = await addComment(formData);

            if (response.success) {
                toast.success(parentId ? "Reply added successfully" : "Comment added successfully");
                setCommentText("");
                setReplyText("");
                setIsInternal(false);
                setFiles([]);
                if (parentId) {
                    setReplyFiles(prev => {
                        const newFiles = { ...prev };
                        delete newFiles[parentId];
                        return newFiles;
                    });
                    setReplyingTo(null);
                }
                
                if (onCommentAdded) {
                    onCommentAdded();
                }
            } else {
                toast.error(response.message || "Failed to add comment");
            }
        } catch (error) {
            console.error("Error posting comment:", error);
            // Extract proper error message from API response
            let errorMessage = "Failed to add comment";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Edit comment
     */
    const handleEdit = (comment) => {
        setEditingId(comment.id);
        setEditText(comment.comment);
    };

    /**
     * Save edited comment
     */
    const saveEdit = async (commentId) => {
        if (!editText.trim()) {
            toast.error("Comment cannot be empty");
            return;
        }

        try {
            const response = await updateComment(commentId, { comment: editText });

            if (response.success) {
                toast.success("Comment updated successfully");
                setEditingId(null);
                setEditText("");
                
                if (onCommentAdded) {
                    onCommentAdded();
                }
            } else {
                toast.error(response.message || "Failed to update comment");
            }
        } catch (error) {
            console.error("Error updating comment:", error);
            // Extract proper error message from API response
            let errorMessage = "Failed to update comment";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        }
    };

    /**
     * Delete comment
     */
    const requestDeleteComment = (commentId) => {
        setDeleteDialog({
            open: true,
            commentId,
        });
    };

    const confirmDeleteComment = async () => {
        if (!deleteDialog.commentId) {
            return;
        }

        try {
            setIsDeletingComment(true);
            const response = await deleteComment(deleteDialog.commentId);

            if (response.success) {
                toast.success("Comment deleted successfully");

                if (onCommentAdded) {
                    onCommentAdded();
                }
            } else {
                toast.error(response.message || "Failed to delete comment");
            }
        } catch (error) {
            console.error("Error deleting comment:", error);
            // Extract proper error message from API response
            let errorMessage = "Failed to delete comment";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setIsDeletingComment(false);
            setDeleteDialog({
                open: false,
                commentId: null,
            });
        }
    };

    /**
     * Render a single comment with modern tree structure (recursive)
     */
    const renderComment = (comment, depth = 0, isLast = false, parentLines = []) => {
        const isAuthor = comment.authorId === user?.id;
        const canEdit = isAuthor;
        const canDelete = isAuthor || isSuperAdmin;
        const maxDepth = 5; // Max nesting level

        return (
            <div key={comment.id} className="comment-wrapper" data-depth={depth}>
                {/* Modern Tree Structure Lines */}
                {depth > 0 && (
                    <div className="tree-lines">
                        {/* Vertical lines from parent levels - GitHub style */}
                        {parentLines.map((shouldShow, idx) => (
                            shouldShow && (
                                <div
                                    key={idx}
                                    className="vertical-line"
                                    style={{ left: `${idx * 32 + 4}px` }}
                                />
                            )
                        ))}
                        
                        {/* Connection line for current comment - GitHub style */}
                        <div
                            className={`connector-line ${isLast ? 'last' : ''}`}
                            style={{ left: `${(depth - 1) * 32 + 4}px` }}
                        />
                    </div>
                )}
                
                <div 
                    className={`comment ${comment.isInternal ? 'internal' : ''}`}
                    style={{
                        marginLeft: depth > 0 ? `${depth * 32 + 8}px` : '0',
                        paddingLeft: depth > 0 ? '16px' : '0',
                        background: comment.isInternal ? '#fffbf0' : '#ffffff',
                        border: depth > 0 
                            ? `1px solid ${comment.isInternal ? '#ffc107' : '#3bac75'}`
                            : comment.isInternal 
                                ? '2px solid #ffc107' 
                                : '1px solid #e5e7eb',
                        borderLeft: depth > 0 ? `3px solid ${comment.isInternal ? '#ffc107' : '#3bac75'}` : 'none',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '12px',
                        position: 'relative',
                        zIndex: 1,
                        boxShadow: depth > 0 
                            ? '0 2px 8px rgba(59, 172, 117, 0.15)' 
                            : '0 1px 3px rgba(0,0,0,0.1)',
                    }}
                >
                    {/* Parent indicator for replies */}
                    {depth > 0 && (
                        <div style={{
                            fontSize: '11px',
                            color: '#6b7280',
                            marginBottom: '8px',
                            paddingBottom: '8px',
                            borderBottom: '1px solid #f3f4f6',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            <i className="las la-reply" style={{ fontSize: '10px' }} />
                            Replying to comment
                        </div>
                    )}
                    {/* Comment Header */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {/* Author Avatar */}
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: comment.authorRole === 'SUPERADMIN' ? '#3bac75' : '#007bff',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: '600',
                                fontSize: '14px',
                            }}>
                                {(comment.authorName || user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                            
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ fontWeight: '600', fontSize: '14px', color: '#212529' }}>
                                        {comment.authorName || user?.name || 'Unknown User'}
                                    </span>
                                    <span style={{
                                        fontSize: '11px',
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        background: comment.authorRole === 'SUPERADMIN' ? '#3bac75' : '#e9ecef',
                                        color: comment.authorRole === 'SUPERADMIN' ? 'white' : '#6c757d',
                                        fontWeight: '600'
                                    }}>
                                        {comment.authorRole === 'SUPERADMIN' ? 'ADMIN' : 'USER'}
                                    </span>
                                    {comment.isInternal && (
                                        <span style={{
                                            background: '#ffc107',
                                            color: '#856404',
                                            padding: '3px 10px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                        }}>
                                            <i className="las la-lock" /> INTERNAL
                                        </span>
                                    )}
                                    {comment.edited && (
                                        <span style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>
                                            (edited)
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: '12px', color: '#adb5bd', marginTop: '2px' }}>
                                    {getTime(comment.created_at || comment.createdAt)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Comment Content */}
                    {editingId === comment.id ? (
                        <div style={{ marginBottom: '12px' }}>
                            <textarea
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '2px solid #3bac75',
                                    minHeight: '80px',
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                    resize: 'vertical',
                                }}
                                autoFocus
                            />
                            <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => saveEdit(comment.id)}
                                    style={{
                                        padding: '8px 16px',
                                        background: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                    }}
                                >
                                    <i className="las la-check" /> Save
                                </button>
                                <button
                                    onClick={() => {
                                        setEditingId(null);
                                        setEditText("");
                                    }}
                                    style={{
                                        padding: '8px 16px',
                                        background: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                    }}
                                >
                                    <i className="las la-times" /> Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div style={{ 
                                fontSize: '14px', 
                                lineHeight: '1.6', 
                                color: '#212529',
                                marginBottom: '12px',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {comment.comment}
                            </div>
                            {/* Display attachments if any */}
                            {comment.attachments && comment.attachments.length > 0 && (
                                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {comment.attachments.map((attachment) => (
                                        <div
                                            key={attachment.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 12px',
                                                background: '#f3f4f6',
                                                borderRadius: '6px',
                                                border: '1px solid #e5e7eb'
                                            }}
                                        >
                                            <i className={getFileIcon(attachment.fileName)} style={{ color: '#3bac75' }} />
                                            <span style={{ fontSize: '13px', color: '#374151', flex: 1 }}>
                                                {attachment.fileName}
                                            </span>
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        const response = await downloadAttachment(attachment.id);
                                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.setAttribute('download', attachment.fileName);
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        link.remove();
                                                        window.URL.revokeObjectURL(url);
                                                    } catch (error) {
                                                        console.error('Error downloading attachment:', error);
                                                        // Extract proper error message from API response
                                                        let errorMessage = 'Failed to download attachment';
                                                        if (error.response?.data?.message) {
                                                            errorMessage = error.response.data.message;
                                                        } else if (error.response?.data?.error) {
                                                            errorMessage = error.response.data.error;
                                                        } else if (error.message) {
                                                            errorMessage = error.message;
                                                        }
                                                        toast.error(errorMessage);
                                                    }
                                                }}
                                                style={{
                                                    padding: '4px 8px',
                                                    background: '#3bac75',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                }}
                                            >
                                                <i className="las la-download" /> Download
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* Comment Actions */}
                    {!editingId && (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {depth < maxDepth && (
                                <button
                                    onClick={() => setReplyingTo(comment.id)}
                                    style={{
                                        padding: '6px 12px',
                                        background: 'transparent',
                                        color: '#007bff',
                                        border: '1px solid #007bff',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                    }}
                                >
                                    <i className="las la-reply" /> Reply
                                </button>
                            )}
                            {canEdit && (
                                <button
                                    onClick={() => handleEdit(comment)}
                                    style={{
                                        padding: '6px 12px',
                                        background: 'transparent',
                                        color: '#3bac75',
                                        border: '1px solid #3bac75',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                    }}
                                >
                                    <i className="las la-edit" /> Edit
                                </button>
                            )}
                            {canDelete && (
                                <button
                                    onClick={() => requestDeleteComment(comment.id)}
                                    style={{
                                        padding: '6px 12px',
                                        background: 'transparent',
                                        color: '#dc3545',
                                        border: '1px solid #dc3545',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                    }}
                                >
                                    <i className="las la-trash" /> Delete
                                </button>
                            )}
                            {comment.repliesCount > 0 && (
                                <span style={{
                                    padding: '6px 12px',
                                    background: '#e9ecef',
                                    color: '#495057',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                }}>
                                    <i className="las la-comments" /> {comment.repliesCount} {comment.repliesCount === 1 ? 'Reply' : 'Replies'}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Reply Input (shown when replying) */}
                    {replyingTo === comment.id && (
                        <div style={{ 
                            marginTop: '16px', 
                            padding: '16px', 
                            background: '#f8f9fa', 
                            borderRadius: '8px',
                            border: '1px solid #dee2e6'
                        }}>
                            <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px' }}>
                                <i className="las la-reply" /> Replying to {comment.authorName || user?.name || 'Unknown User'}
                            </div>
                            <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write your reply..."
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid #ced4da',
                                    minHeight: '80px',
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                    resize: 'vertical',
                                }}
                            />
                            {/* File Upload for Reply */}
                            <div style={{ marginTop: '10px' }}>
                                <label style={{
                                    display: 'inline-block',
                                    padding: '6px 12px',
                                    background: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    color: '#495057',
                                }}>
                                    <i className="las la-paperclip" /> Attach Files
                                    <input
                                        type="file"
                                        multiple
                                        accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp4,.mov,.avi,.mkv,.webm,.mp3,.wav,.m4v"
                                        onChange={(e) => handleFileChange(e, true)}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            </div>
                            {/* Display selected files for reply */}
                            {replyFiles[comment.id] && replyFiles[comment.id].length > 0 && (
                                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {replyFiles[comment.id].map((file, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '8px 12px',
                                                background: '#f3f4f6',
                                                borderRadius: '6px',
                                                fontSize: '12px'
                                            }}
                                        >
                                            <span>
                                                <i className={getFileIcon(file.name)} style={{ color: '#3bac75', marginRight: '6px' }} />
                                                {file.name}
                                            </span>
                                            <button
                                                onClick={() => removeFile(index, true, comment.id)}
                                                style={{
                                                    background: 'transparent',
                                                    color: '#dc2626',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                }}
                                            >
                                                <i className="las la-times" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => postComment(comment.id)}
                                    disabled={submitting || (!replyText.trim() && (!replyFiles[comment.id] || replyFiles[comment.id].length === 0))}
                                    style={{
                                        padding: '8px 16px',
                                        background: submitting || (!replyText.trim() && (!replyFiles[comment.id] || replyFiles[comment.id].length === 0)) ? '#6c757d' : '#3bac75',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: submitting || (!replyText.trim() && (!replyFiles[comment.id] || replyFiles[comment.id].length === 0)) ? 'not-allowed' : 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                    }}
                                >
                                    {submitting ? <><i className="las la-spinner la-spin" /> Posting...</> : <><i className="las la-paper-plane" /> Post Reply</>}
                                </button>
                                <button
                                    onClick={() => {
                                        setReplyingTo(null);
                                        setReplyText("");
                                        setReplyFiles(prev => {
                                            const newFiles = { ...prev };
                                            delete newFiles[comment.id];
                                            return newFiles;
                                        });
                                    }}
                                    style={{
                                        padding: '8px 16px',
                                        background: 'white',
                                        color: '#6c757d',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Render Nested Replies (Recursive with Tree Structure) */}
                {comment.replies && comment.replies.length > 0 && (
                    <div className="replies-container">
                        {comment.replies.map((reply, index) => {
                            const newParentLines = [...parentLines, !isLast];
                            return renderComment(
                                reply, 
                                depth + 1, 
                                index === comment.replies.length - 1,
                                newParentLines
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="comment-box-modern">
            {/* Tabs Section */}
            <div style={{
                background: 'white',
                borderRadius: '12px 12px 0 0',
                padding: '0',
                marginBottom: '0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderBottom: '1px solid #e5e7eb',
            }}>
                <div style={{
                    display: 'inline-flex',
                    gap: '0',
                    borderBottom: 'none',
                }}>
                    <button
                        onClick={() => setActiveTab("comments")}
                        style={{
                            padding: '10px 20px',
                            background: activeTab === "comments" ? '#3bac75' : 'transparent',
                            color: activeTab === "comments" ? 'white' : '#6b7280',
                            border: 'none',
                            borderBottom: activeTab === "comments" ? '2px solid #3bac75' : '2px solid transparent',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: activeTab === "comments" ? '600' : '500',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            borderRadius: '0',
                            position: 'relative',
                        }}
                    >
                        <i className="las la-comments" style={{ fontSize: '14px' }} /> 
                        <span>Comments</span>
                        <span style={{ 
                            marginLeft: '4px',
                            fontSize: '11px',
                            opacity: 0.8,
                            fontWeight: '500'
                        }}>
                            ({ticket?.comments?.length || 0})
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab("activity")}
                        style={{
                            padding: '10px 20px',
                            background: activeTab === "activity" ? '#3bac75' : 'transparent',
                            color: activeTab === "activity" ? 'white' : '#6b7280',
                            border: 'none',
                            borderBottom: activeTab === "activity" ? '2px solid #3bac75' : '2px solid transparent',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: activeTab === "activity" ? '600' : '500',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            borderRadius: '0',
                        }}
                    >
                        <i className="las la-history" style={{ fontSize: '14px' }} /> 
                        <span>Activity Log</span>
                        <span style={{ 
                            marginLeft: '4px',
                            fontSize: '11px',
                            opacity: 0.8,
                            fontWeight: '500'
                        }}>
                            ({activityLogs.length})
                        </span>
                    </button>
                </div>
            </div>

            {/* Tab Content Container - Prevents layout shift */}
            <div style={{
                position: 'relative',
                minHeight: '500px',
                transition: 'all 0.3s ease-in-out',
            }}>
                {activeTab === "comments" ? (
                    <>
                        {/* Add Comment Section */}
                        <div className="add-comment-section" style={{
                            background: 'white',
                            borderRadius: '0 0 12px 12px',
                            padding: '20px',
                            marginBottom: '24px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        }}>
                <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600', color: '#212529' }}>
                    <i className="las la-comment-dots" /> Add Comment
                </h3>

                {/* User Info Display - DATA FROM AUTH API */}
                <div style={{ 
                    background: '#f8f9fa', 
                    padding: '12px 16px', 
                    borderRadius: '8px', 
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#495057',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: isSuperAdmin ? '#3bac75' : '#007bff',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '600',
                        fontSize: '13px',
                    }}>
                        {user?.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div>
                        <div style={{ fontWeight: '600' }}>Commenting as: {user?.name}</div>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>{user?.email}</div>
                    </div>
                </div>

                {/* Internal Note Toggle (SUPERADMIN only) */}
                {isSuperAdmin && (
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px', 
                            cursor: 'pointer',
                            padding: '10px',
                            background: isInternal ? '#fff3cd' : '#f8f9fa',
                            borderRadius: '8px',
                            border: isInternal ? '1px solid #ffc107' : '1px solid #dee2e6',
                        }}>
                            <input
                                type="checkbox"
                                checked={isInternal}
                                onChange={(e) => setIsInternal(e.target.checked)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '13px', fontWeight: '500', color: isInternal ? '#856404' : '#495057' }}>
                                <i className="las la-lock" /> Internal Note (Only visible to admins)
                            </span>
                        </label>
                    </div>
                )}

                {/* Comment Input */}
                <textarea
                    placeholder="Write a comment..."
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    style={{
                        width: '100%',
                        padding: '14px',
                        borderRadius: '8px',
                        border: '2px solid #dee2e6',
                        resize: 'vertical',
                        minHeight: '100px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3bac75'}
                    onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                />

                {/* File Upload */}
                <div style={{ marginTop: '12px' }}>
                    <label style={{
                        display: 'inline-block',
                        padding: '8px 16px',
                        background: '#f8f9fa',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        color: '#495057',
                        transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e9ecef';
                        e.currentTarget.style.borderColor = '#3bac75';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#dee2e6';
                    }}
                    >
                        <i className="las la-paperclip" /> Attach Files
                        <input
                            type="file"
                            multiple
                            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp4,.mov,.avi,.mkv,.webm,.mp3,.wav,.m4v"
                            onChange={(e) => handleFileChange(e, false)}
                            style={{ display: 'none' }}
                        />
                    </label>
                    <small style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: '#6c757d' }}>
                        Max 5 files, 50MB each. Supported: images, PDFs, documents, videos
                    </small>
                </div>

                {/* Display selected files */}
                {files.length > 0 && (
                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {files.map((file, index) => (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px 14px',
                                    background: '#f3f4f6',
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb'
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151' }}>
                                    <i className={getFileIcon(file.name)} style={{ color: '#3bac75' }} />
                                    {file.name} <span style={{ color: '#6b7280' }}>({(file.size / 1024).toFixed(2)} KB)</span>
                                </span>
                                <button
                                    onClick={() => removeFile(index, false)}
                                    style={{
                                        background: 'transparent',
                                        color: '#dc2626',
                                        border: '1px solid #dc2626',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                    }}
                                >
                                    <i className="las la-times" /> Remove
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <button
                    onClick={() => postComment()}
                    disabled={submitting || (!commentText.trim() && files.length === 0)}
                    style={{
                        marginTop: '12px',
                        padding: '12px 24px',
                        background: submitting || (!commentText.trim() && files.length === 0) ? '#6c757d' : '#3bac75',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: submitting || (!commentText.trim() && files.length === 0) ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'background 0.2s',
                    }}
                >
                    {submitting ? (
                        <>
                            <i className="las la-spinner la-spin" /> Posting...
                        </>
                    ) : (
                        <>
                            <i className="las la-paper-plane" /> Post Comment
                        </>
                    )}
                </button>
            </div>

                    {/* Comments List - ALL DATA FROM API */}
                    <div className="comments-list" style={{
                        background: 'white',
                        borderRadius: '0 0 12px 12px',
                        padding: '24px',
                        marginBottom: '24px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}>
                        {ticket?.comments && ticket.comments.length > 0 ? (
                    <div className="comments-tree">
                        {(() => {
                            // Build tree structure from flat comments
                            const commentMap = {};
                            const rootComments = [];
                            
                            // First pass: create map and add replies array
                            ticket.comments.forEach(comment => {
                                commentMap[comment.id] = { ...comment, replies: [] };
                            });
                            
                            // Second pass: build tree
                            ticket.comments.forEach(comment => {
                                if (comment.parentCommentId && commentMap[comment.parentCommentId]) {
                                    // This is a reply, add to parent's replies
                                    commentMap[comment.parentCommentId].replies.push(commentMap[comment.id]);
                                } else if (!comment.parentCommentId) {
                                    // This is a root comment
                                    rootComments.push(commentMap[comment.id]);
                                }
                            });
                            
                            // Render only root comments (recursion will handle replies)
                            return rootComments.map((comment, index) => 
                                renderComment(comment, 0, index === rootComments.length - 1, [])
                            );
                        })()}
                    </div>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        background: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}>
                        <i className="las la-comment-slash" style={{ 
                            fontSize: '64px', 
                            display: 'block', 
                            marginBottom: '16px',
                            color: '#dee2e6'
                        }} />
                        <p style={{ fontSize: '16px', color: '#6c757d', fontWeight: '500' }}>No comments yet</p>
                        <p style={{ fontSize: '13px', color: '#adb5bd', marginTop: '8px' }}>
                            Be the first to comment on this ticket!
                        </p>
                    </div>
                        )}
                    </div>
                    </>
                ) : (
                    /* Activity Logs Tab */
                    <div style={{
                        background: 'white',
                        borderRadius: '0 0 12px 12px',
                        padding: '24px',
                        marginBottom: '24px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        minHeight: '500px',
                    }}>
                        {loadingLogs ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                                <i className="las la-spinner la-spin" style={{ fontSize: '24px' }} /> Loading...
                            </div>
                        ) : activityLogs.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                                <i className="las la-history" style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                                <p>No activity yet</p>
                            </div>
                        ) : (
                            <div className="activity-timeline">
                                {activityLogs.map((log) => (
                                    <div key={log.id} className="activity-item">
                                        <div className="activity-icon" style={{ background: getActionColor(log.action) }}>
                                            <i className={getActionIcon(log.action)} />
                                        </div>
                                        <div className="activity-content">
                                            <div className="activity-header">
                                                <span className="activity-user">{log.userName}</span>
                                                <span className="activity-time">{getTime(log.created_at || log.createdAt)}</span>
                                            </div>
                                            <div className="activity-description">
                                                {log.description}
                                                {log.oldValue && log.newValue && (
                                                    <span className="activity-change">
                                                        {" "}{log.oldValue} → {log.newValue}
                                                    </span>
                                                )}
                                            </div>
                                            {log.metadata && log.metadata.files && log.metadata.files.length > 0 && (
                                                <div className="activity-attachment">
                                                    <i className="las la-paperclip" /> {log.metadata.files.map(f => f.name || f).join(', ')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <ConfirmDialog
                open={deleteDialog.open}
                title="Delete this comment?"
                message="This action will remove the comment and any replies associated with it."
                confirmLabel="Delete Comment"
                cancelLabel="Keep Comment"
                loading={isDeletingComment}
                onConfirm={confirmDeleteComment}
                onCancel={() => {
                    if (!isDeletingComment) {
                        setDeleteDialog({
                            open: false,
                            commentId: null,
                        });
                    }
                }}
            />
        </div>
    );
};

export default CommentBox;
